import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./Home.css";
import HomeImage from "../assets/Home.jpg";
import HomeVideo from "../assets/Services1.mp4";

const Home = () => {
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const heroRef = useRef(null);
  const videoRef = useRef(null);

  useEffect(() => {
    initParticleAnimation();
    initFloatingAnimations();
    initScrollAnimations();
    
    // Play video on load
    if (videoRef.current) {
      videoRef.current.play().catch(e => console.log("Video autoplay prevented:", e));
    }
    
    return () => {
      if (canvasRef.current) {
        const ctx = canvasRef.current.getContext('2d');
        ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
      }
    };
  }, []);

  const initParticleAnimation = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    
    let particles = [];
    const particleCount = 100;

    for (let i = 0; i < particleCount; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        size: Math.random() * 2 + 0.5,
        speedX: (Math.random() - 0.5) * 0.5,
        speedY: (Math.random() - 0.5) * 0.5,
        color: Math.random() > 0.5 ? 'rgba(0, 115, 255, 0.6)' : 'rgba(41, 128, 185, 0.6)'
      });
    }

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      particles.forEach(p => {
        p.x += p.speedX;
        p.y += p.speedY;
        
        if (p.x > canvas.width) p.x = 0;
        if (p.x < 0) p.x = canvas.width;
        if (p.y > canvas.height) p.y = 0;
        if (p.y < 0) p.y = canvas.height;
        
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = p.color;
        ctx.fill();
      });
      
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          
          if (distance < 100) {
            ctx.beginPath();
            ctx.strokeStyle = `rgba(0, 115, 255, ${0.1 * (1 - distance/100)})`;
            ctx.lineWidth = 0.3;
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }
      }
      
      requestAnimationFrame(animate);
    };
    
    animate();

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  };

  const initFloatingAnimations = () => {
    const floatElements = document.querySelectorAll('.floating-element');
    floatElements.forEach((el, index) => {
      el.style.animationDelay = `${index * 0.2}s`;
    });
  };

  const initScrollAnimations = () => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
        }
      });
    }, observerOptions);

    document.querySelectorAll('[data-animate]').forEach(el => {
      observer.observe(el);
    });
  };

  const services = [
    {
      id: 1,
      title: "Workday Advisory",
      description: "Strategic guidance for successful Workday implementation and optimization.",
      icon: "🎯",
      color: "linear-gradient(135deg, #0073ff 0%, #2980b9 100%)"
    },
    {
      id: 2,
      title: "Workday AMS",
      description: "24/7 Application Management Services for uninterrupted operations.",
      icon: "🛡️",
      color: "linear-gradient(135deg, #3498db 0%, #2c3e50 100%)"
    },
    {
      id: 3,
      title: "Workday Experts on Demand",
      description: "Access certified Workday experts whenever you need them.",
      icon: "👨‍💼",
      color: "linear-gradient(135deg, #1abc9c 0%, #16a085 100%)"
    },
    {
      id: 4,
      title: "Workday Test Automation",
      description: "Automated testing solutions ensuring flawless deployments.",
      icon: "🤖",
      color: "linear-gradient(135deg, #9b59b6 0%, #8e44ad 100%)"
    },
    {
      id: 5,
      title: "Workday Certifications",
      description: "Comprehensive certification programs for your team.",
      icon: "🏆",
      color: "linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)"
    },
    {
      id: 6,
      title: "Workday Training",
      description: "Customized training programs for all user levels.",
      icon: "📚",
      color: "linear-gradient(135deg, #f39c12 0%, #d35400 100%)"
    }
  ];

  return (
    <div className="home">
      {/* Particle Background */}
      <canvas ref={canvasRef} className="particle-canvas"></canvas>

      {/* ===== MAIN HERO SECTION ===== */}
      <section className="main-hero-section" ref={heroRef}>
        {/* Video Background */}
        <div className="video-background">
          <video
            ref={videoRef}
            className="hero-video"
            autoPlay
            loop
            muted
            playsInline
          >
            <source src={HomeVideo} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
          <div className="video-overlay"></div>
        </div>
        
        {/* Background Orbs */}
        <div className="background-orb orb-1"></div>
        <div className="background-orb orb-2"></div>
        <div className="background-orb orb-3"></div>
        
        <div className="main-hero-container">
          {/* Full Width Content - Centered */}
          <div className="hero-full-content">
            {/* Brand Logo Section - Centered */}
            <div className="brand-section-centered">
              <div className="brand-logo-centered">
                
               
              </div>
            </div>

            {/* Main Hero Title */}
            <div className="hero-title-section">
              <h1 className="hero-main-title">
                Transform Your
                <span className="hero-title-highlighted">Workday Experience</span>
              </h1>
            </div>

            {/* Hero Description */}
            <div className="hero-description-centered">
              <p className="hero-desc-text">
                Comprehensive solutions designed to maximize your Workday investment 
                and drive business transformation
              </p>
            </div>

            {/* Stats Grid - Centered with White Cards */}
            <div className="stats-grid-centered">
              <div className="stats-row">
                {/* Projects Completed */}
                <div className="stat-card-centered">
                  <div className="stat-number-white">50+</div>
                  <div className="stat-label-white">Projects Completed</div>
                </div>
                
                {/* Client Satisfaction */}
                <div className="stat-card-centered">
                  <div className="stat-number-white">99%</div>
                  <div className="stat-label-white">Client Satisfaction</div>
                </div>
                
                {/* Certified Experts */}
                <div className="stat-card-centered">
                  <div className="stat-number-white">50+</div>
                  <div className="stat-label-white">Certified Experts</div>
                </div>
                
                {/* Global Support */}
                <div className="stat-card-centered">
                  <div className="stat-number-white">24/7</div>
                  <div className="stat-label-white">Global Support</div>
                </div>
              </div>
            </div>

            {/* CTA Buttons - Centered */}
            <div className="hero-buttons-centered">
              <button 
                className="btn-explore-centered"
                onClick={() => navigate("/services")}
              >
                <span className="btn-text">Explore Services</span>
                <span className="btn-icon">→</span>
              </button>
              
              <button 
                className="btn-contact-centered"
                onClick={() => navigate("/contact")}
              >
                Get Started
              </button>
            </div>
          </div>
        </div>
        
        {/* Scroll Indicator */}
        <div className="scroll-indicator">
          <div className="scroll-line"></div>
          <div className="scroll-dot"></div>
          <span className="scroll-text">Scroll to explore</span>
        </div>
      </section>

      {/* ===== WELCOME SECTION ===== */}
      <section className="welcome-section">
        <div className="welcome-container">
          <div className="welcome-header" data-animate="fade-up">
            <h2 className="welcome-title">
              Welcome to <span className="brand-name">JCRL CORNERSTONE TECHNOLOGIES</span>
            </h2>
          </div>
          
          <div className="welcome-content">
            <div className="welcome-text" data-animate="slide-left">
              <p className="welcome-description">
                Your trusted partner for <span className="highlights">Customized Solutions</span>, 
                <span className="highlights"> Expert Support</span>, and 
                <span className="highlights"> Seamless Integration</span> of Workday HCM and Financial Management.
              </p>

              <p className="welcome-subdescription">
                Enhance your experience with effortless engagement. Step into a bespoke partnership 
                aligned with your goals and aspirations, where you remain in control, and we are here 
                to support you whenever you need us.
              </p>

              <div className="welcome-features">
                <div className="feature-item">
                  <div className="feature-icon">✓</div>
                  <div className="feature-content">
                    <h4>Expert Guidance</h4>
                    <p>Industry-leading Workday consultants</p>
                  </div>
                </div>
                
                <div className="feature-item">
                  <div className="feature-icon">⚡</div>
                  <div className="feature-content">
                    <h4>Rapid Deployment</h4>
                    <p>Quick implementation with agile methodology</p>
                  </div>
                </div>
                
                <div className="feature-item">
                  <div className="feature-icon">🛡️</div>
                  <div className="feature-content">
                    <h4>Secure Solutions</h4>
                    <p>Enterprise-grade security and compliance</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Home Image */}
            <div 
              className="welcome-image-container"
              style={{ height: "auto", display: "inline-block" }}
            >
              <img 
                src={HomeImage} 
                alt="JCRL Cornerstone Technologies"
                className="welcome-image"
                style={{
                  width: "100%",
                  height: "auto",
                  display: "block"
                }}
              />
              
              <div 
                className="image-frame"
                style={{
                  top: "0",
                  bottom: "0",
                  left: "0",
                  right: "0",
                  border: "3px solid rgba(0,115,255,0.3)",
                  borderRadius: "20px",
                  pointerEvents: "none"
                }}
              ></div>
              
              <div className="image-glow"></div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== SERVICES SECTION ===== */}
      <section className="services-section">
        <div className="services-header" data-animate="fade-up">
          <h2 className="services-title">Our Core Services</h2>
          <p className="services-subtitle">
            Comprehensive Workday solutions to transform your business operations
          </p>
        </div>
        
        <div className="services-grid">
          {services.map((service, index) => (
            <div 
              key={service.id} 
              className="service-card"
              data-animate="zoom-in"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="service-card-inner">
                <div className="service-icon-container" style={{ background: service.color }}>
                  <span className="service-icon">{service.icon}</span>
                </div>
                <h3 className="service-card-title">{service.title}</h3>
                <p className="service-card-description">{service.description}</p>
                <button 
                  className="service-card-button"
                  onClick={() => navigate("/services")}
                >
                  Learn More →
                </button>
              </div>
            </div>
          ))}
        </div>
        
        <div className="services-cta" data-animate="fade-up">
          <button 
            className="btn-view-all"
            onClick={() => navigate("/services")}
          >
            View All Services
          </button>
        </div>
      </section>

      {/* ===== CTA SECTION ===== */}
      <section className="cta-section">
        <div className="cta-container" data-animate="zoom-in">
          <div className="cta-content">
            <div className="cta-icon">🚀</div>
            <h2 style={{ color: "#0ddbfc" }}>
              Ready to Transform Your Business?
            </h2>

            <p className="cta-description">
              Let's discuss how JCRL can help you achieve your goals with our expert Workday solutions.
            </p>
            
            <div className="cta-buttons">
              <button 
                className="btn-cta-primary"
                onClick={() => navigate("/contact")}
              >
                Get in Touch
              </button>
              
              <button 
                className="btn-cta-secondary"
                onClick={() => navigate("/about")}
              >
                Learn More About Us
              </button>
            </div>
          </div>
        </div>
        
        <div className="cta-background">
          <div className="cta-wave"></div>
        </div>
      </section>
    </div>
  );
};

export default Home;